import { useState } from 'react';
import { ClipboardList, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAttendanceReport,
  useReportCoaches,
  useReportPlayers,
  exportAttendanceCSV,
} from '@/hooks/use-director-data';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from 'recharts';
import { AttendanceSummaryCards } from '@/components/director/attendance-report/attendance-summary-cards';
import { AttendanceTable } from '@/components/director/attendance-report/attendance-table';
import { AttendanceFilters } from '@/components/director/attendance-report/attendance-filters';

function getDefaultDateRange() {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return {
    from: thirtyDaysAgo.toISOString().split('T')[0],
    to: today.toISOString().split('T')[0],
  };
}

export default function AttendanceReportPage() {
  const defaultRange = getDefaultDateRange();
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);
  const [coachFilter, setCoachFilter] = useState('');
  const [playerFilter, setPlayerFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const filters: Record<string, string | undefined> = {
    fromDate,
    toDate,
  };
  if (coachFilter) filters.coachUserId = coachFilter;
  if (playerFilter) filters.playerId = playerFilter;
  if (typeFilter) filters.sessionType = typeFilter;

  const { data, isLoading } = useAttendanceReport(filters);
  const { data: coaches } = useReportCoaches();
  const { data: players } = useReportPlayers();

  const handleExportCSV = () => {
    if (!data?.rawRecords) return;
    exportAttendanceCSV(data.rawRecords);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const summary = data?.summary;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-primary" />
            Attendance Report
          </h1>
          <p className="text-muted-foreground">Detailed attendance analytics and export</p>
        </div>
        <Button onClick={handleExportCSV} disabled={!data?.rawRecords?.length} variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Date Range + Filters */}
      <AttendanceFilters
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        coachFilter={coachFilter}
        playerFilter={playerFilter}
        typeFilter={typeFilter}
        onCoachFilterChange={setCoachFilter}
        onPlayerFilterChange={setPlayerFilter}
        onTypeFilterChange={setTypeFilter}
        coaches={coaches}
        players={players}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
      />

      {/* Summary Stats */}
      <AttendanceSummaryCards
        totalSessions={summary?.totalSessions ?? 0}
        attendanceRate={summary?.attendanceRate ?? 0}
        lateRate={summary?.lateRate ?? 0}
        absentCount={summary?.absentCount ?? 0}
      />

      {/* Status Breakdown Bar */}
      {summary && summary.totalRecords > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4 mb-2">
              <span className="text-sm font-medium text-foreground">Status Breakdown</span>
              <span className="text-xs text-muted-foreground">{summary.totalRecords} records</span>
            </div>
            <div className="flex h-4 rounded-full overflow-hidden">
              {summary.presentCount > 0 && (
                <div
                  className="bg-green-500"
                  style={{ width: `${(summary.presentCount / summary.totalRecords) * 100}%` }}
                  title={`Present: ${summary.presentCount}`}
                />
              )}
              {summary.lateCount > 0 && (
                <div
                  className="bg-yellow-400"
                  style={{ width: `${(summary.lateCount / summary.totalRecords) * 100}%` }}
                  title={`Late: ${summary.lateCount}`}
                />
              )}
              {summary.absentCount > 0 && (
                <div
                  className="bg-red-400"
                  style={{ width: `${(summary.absentCount / summary.totalRecords) * 100}%` }}
                  title={`Absent: ${summary.absentCount}`}
                />
              )}
              {summary.excusedCount > 0 && (
                <div
                  className="bg-gray-300"
                  style={{ width: `${(summary.excusedCount / summary.totalRecords) * 100}%` }}
                  title={`Excused: ${summary.excusedCount}`}
                />
              )}
            </div>
            <div className="flex gap-4 mt-2 text-xs">
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-500" /> Present ({summary.presentCount})
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-yellow-400" /> Late ({summary.lateCount})
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-400" /> Absent ({summary.absentCount})
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-gray-300" /> Excused ({summary.excusedCount})
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attendance Trend Chart */}
      {data?.dailyTrends && data.dailyTrends.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Attendance Rate Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={data.dailyTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="rate"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  name="Attendance %"
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Session Type Breakdown */}
      {data?.sessionTypeBreakdown && data.sessionTypeBreakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">By Session Type</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.sessionTypeBreakdown}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="present" fill="#22c55e" name="Present" stackId="a" />
                <Bar dataKey="late" fill="#facc15" name="Late" stackId="a" />
                <Bar dataKey="absent" fill="#f87171" name="Absent" stackId="a" />
                <Bar dataKey="excused" fill="#d1d5db" name="Excused" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Player Breakdown Table */}
      {data?.playerBreakdown && (
        <AttendanceTable playerBreakdown={data.playerBreakdown} />
      )}

      {/* Empty state */}
      {summary?.totalRecords === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-muted-foreground">No attendance records found for the selected period</p>
            <p className="text-sm text-muted-foreground mt-1">Try adjusting the date range or filters</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
